import React, { useEffect, useMemo, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { toast } from "sonner";
import { X, Loader2, Plus, Trash2 } from "lucide-react";

const CUR_SYMBOL = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const money = (minor, cur) => `${CUR_SYMBOL[cur] || cur + " "}${(Number(minor || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PRESETS = [
  { key: "full",     label: "Pay in full",  split: [1] },
  { key: "50_50",    label: "50 / 50",       split: [0.5, 0.5],       labels: ["Deposit", "Final"] },
  { key: "40_30_30", label: "40 / 30 / 30",  split: [0.4, 0.3, 0.3],  labels: ["Deposit", "Interim", "Final"] },
  { key: "custom",   label: "Custom",        split: null },
];

const SERVICE_TEMPLATES = [
  "Custom Website Development",
  "CRM System Build",
  "Landing Page Design",
  "Orange Optimise Annual Monitoring",
  "OrangeCare Website Support",
];

// Phase 3H Part B — service_type canonical keys (must match backend
// AGREEMENT_TYPE_BY_SERVICE). Presented to the operator when they toggle
// "This is a bespoke project".
const AGREEMENT_SERVICE_TYPES = [
  { key: "premium_website",     label: "Custom Website Development" },
  { key: "crm_business_system", label: "CRM & Business Systems" },
  { key: "landing_page",        label: "Landing Page" },
  { key: "orange_fix",          label: "Orange Fix" },
];

const SERVICE_KEY_LABEL = {
  premium_website:     "Custom Website Development",
  crm_business_system: "CRM System Build",
  landing_page:        "Landing Page Design",
  orange_optimise:     "Orange Optimise Annual Monitoring",
  orange_care:         "OrangeCare Website Support",
};

/**
 * CreateInvoiceModal — Phase 2C.
 *
 * Full invoice builder that opens OVER the Client Profile. Prefills
 * business/contact/email/currency from the client. Never navigates away.
 * Uses the existing Stage 6A POST /api/hq/invoices endpoint under the
 * hood — no duplicate invoice models are introduced.
 */
export default function CreateInvoiceModal({
  open, onClose, businessId, contacts, primaryEmail, businessName,
  currency: defaultCurrency, serviceSlots, onCreated,
}) {
  const [contactId, setContactId] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [description, setDescription] = useState("");
  const [issueDate, setIssueDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [currency, setCurrency] = useState((defaultCurrency || "GBP").toUpperCase());
  const [total, setTotal] = useState("1000.00");
  const [notes, setNotes] = useState("");
  const [preset, setPreset] = useState("50_50");
  const [installments, setInstallments] = useState([]);
  const [saving, setSaving] = useState(false);
  // Phase 3H Part B — project agreement
  const [asProject, setAsProject]           = useState(false);
  const [projectServiceType, setProjectSvc] = useState("premium_website");
  const [projectTitle,        setProjectTitle]        = useState("");
  const [projectScope,        setProjectScope]        = useState("");
  const [projectDeliverables, setProjectDeliverables] = useState("");
  const [projectExclusions,   setProjectExclusions]   = useState("");
  const [projectCountry,      setProjectCountry]      = useState("GB");

  const contactList = useMemo(() => Array.isArray(contacts) ? contacts : [], [contacts]);

  // Prefill on open
  useEffect(() => {
    if (!open) return;
    const first = contactList[0] || {};
    setContactId(first.id || "");
    setToEmail(first.email || primaryEmail || "");
    setDescription("");
    setIssueDate(new Date().toISOString().slice(0, 10));
    setDueDate("");
    setCurrency((defaultCurrency || "GBP").toUpperCase());
    setTotal("1000.00");
    setNotes("");
    setPreset("50_50");
  }, [open, contactList, primaryEmail, defaultCurrency]);

  useEffect(() => {
    // Update email when contact selection changes
    if (!open) return;
    const c = contactList.find((x) => x.id === contactId);
    if (c && c.email) setToEmail(c.email);
  }, [contactId, contactList, open]);

  // Recalculate installments when preset or total changes
  const totalMinor = Math.round(parseFloat(total || "0") * 100);
  useEffect(() => {
    if (!open) return;
    if (preset === "custom") return; // don't overwrite custom rows
    const p = PRESETS.find((x) => x.key === preset);
    if (!p || !p.split) return;
    const minors = p.split.map((s) => Math.round(s * totalMinor));
    const drift = totalMinor - minors.reduce((a, b) => a + b, 0);
    if (minors.length) minors[minors.length - 1] += drift;
    setInstallments(p.split.map((_, i) => ({
      label: (p.labels || [])[i] || (p.split.length === 1 ? "Full payment" : `Payment ${i + 1}`),
      amount: (minors[i] / 100).toFixed(2),
      due_date: "",
      description: "",
    })));
  }, [preset, totalMinor, open]);

  const scheduledMinor = installments.reduce(
    (a, i) => a + Math.round(parseFloat(i.amount || "0") * 100), 0,
  );
  const remaining = totalMinor - scheduledMinor;
  const scheduleValid = totalMinor > 0
    && remaining === 0
    && installments.length > 0
    && installments.every((i) => i.label && Math.round(parseFloat(i.amount || "0") * 100) > 0);

  const provider = currency === "AED" ? "Stripe" : "PayPal";

  const clientServiceKeys = (serviceSlots || []).map((s) => s.service_key);
  const suggestions = [
    ...clientServiceKeys.map((k) => SERVICE_KEY_LABEL[k]).filter(Boolean),
    ...SERVICE_TEMPLATES.filter((t) => !clientServiceKeys.map((k) => SERVICE_KEY_LABEL[k]).includes(t)),
  ];

  const addRow = () => setInstallments([...installments,
    { label: `Payment ${installments.length + 1}`, amount: "0.00", due_date: "", description: "" }]);
  const removeRow = (idx) => setInstallments(installments.filter((_, i) => i !== idx));
  const patchRow = (idx, patch) => setInstallments(installments.map((x, i) => i === idx ? { ...x, ...patch } : x));

  const saveDraft = async () => {
    if (!scheduleValid) return toast.error("Schedule must sum to invoice total");
    if (!toEmail) return toast.error("Recipient email is required");
    setSaving(true);
    try {
      const presetMap = { full: "PAY_IN_FULL", "50_50": "50_50", "40_30_30": "40_30_30", custom: "CUSTOM" };
      const projectAgreement = asProject ? {
        title:        projectTitle || description,
        service_type: projectServiceType,
        description:  description || "",
        scope:        projectScope.split("\n").map((s) => s.trim()).filter(Boolean),
        deliverables: projectDeliverables.split("\n").map((s) => s.trim()).filter(Boolean),
        exclusions:   projectExclusions.split("\n").map((s) => s.trim()).filter(Boolean),
        special_terms: "",
        customer_country: projectCountry || null,
      } : null;
      const payload = {
        business_id: businessId,
        contact_id: contactId || null,
        currency,
        total_amount_minor: totalMinor,
        description,
        notes,
        issue_date: issueDate || null,
        due_date: dueDate || null,
        installments: installments.map((i) => ({
          label: i.label,
          amount_minor: Math.round(parseFloat(i.amount || "0") * 100),
          due_date: i.due_date || null,
          description: i.description || "",
        })),
        project_agreement: projectAgreement,
        payment_schedule_preset: presetMap[preset] || preset.toUpperCase(),
      };
      const r = await hqApi.post("/invoices", payload);
      toast.success(`Draft ${r.data.invoice_number} saved`);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save draft");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="hq-create-invoice-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">New Invoice</div>
            <h2 className="font-black text-lg mt-1">Create invoice</h2>
          </div>
          <button data-testid="hq-create-invoice-close" onClick={onClose} className="p-2 rounded-full hover:bg-[#F6F5F2]">
            <X size={16}/>
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Client">
              <div className="px-3 py-2 rounded-lg bg-[#F6F5F2] font-bold">{businessName || "—"}</div>
            </Field>
            <Field label="Contact">
              <select
                data-testid="hq-create-invoice-contact"
                value={contactId} onChange={(e) => setContactId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border"
              >
                <option value="">(No specific contact)</option>
                {contactList.map((c) => (
                  <option key={c.id} value={c.id}>{c.full_name || c.email}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Email">
            <input
              data-testid="hq-create-invoice-email"
              type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border"
            />
          </Field>

          <Field label="Description / Service">
            <input
              data-testid="hq-create-invoice-description"
              value={description} onChange={(e) => setDescription(e.target.value)}
              list="hq-invoice-service-suggestions"
              placeholder="Custom Website Development"
              className="w-full px-3 py-2 rounded-lg border"
            />
            <datalist id="hq-invoice-service-suggestions">
              {suggestions.map((s) => <option key={s} value={s}/>)}
            </datalist>
            {clientServiceKeys.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {clientServiceKeys.map((k) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`hq-create-invoice-svc-${k}`}
                    onClick={() => setDescription(SERVICE_KEY_LABEL[k] || k)}
                    className="text-[10px] tracking-[0.14em] uppercase font-black px-2 py-0.5 rounded-full bg-[#FFF7EE] border border-[#FCE1C7] text-[#8A4A00] hover:bg-[#FFE7CD]"
                  >
                    Use: {SERVICE_KEY_LABEL[k] || k}
                  </button>
                ))}
              </div>
            )}
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Field label="Issue date">
              <input data-testid="hq-create-invoice-issue" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="w-full px-3 py-2 rounded-lg border"/>
            </Field>
            <Field label="Due date">
              <input data-testid="hq-create-invoice-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-3 py-2 rounded-lg border"/>
            </Field>
            <Field label="Currency">
              <select
                data-testid="hq-create-invoice-currency"
                value={currency} onChange={(e) => setCurrency(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border"
              >
                {["GBP","USD","EUR","AED"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="text-[10px] mt-1 text-[#8A8A8A]">Provider auto: <strong>{provider}</strong></div>
            </Field>
            <Field label="Total">
              <input data-testid="hq-create-invoice-total" inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} className="w-full px-3 py-2 rounded-lg border font-black"/>
            </Field>
          </div>

          <Field label="Payment schedule">
            <div className="flex flex-wrap gap-2 mb-2">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  data-testid={`hq-create-invoice-preset-${p.key}`}
                  type="button"
                  onClick={() => {
                    setPreset(p.key);
                    if (p.key === "custom" && installments.length === 0) {
                      setInstallments([{ label: "Payment 1", amount: (totalMinor/100).toFixed(2), due_date: "", description: "" }]);
                    }
                  }}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.14em] uppercase border ${preset === p.key ? "bg-black text-white border-black" : "bg-white border-[#EAEAEA]"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {installments.map((inst, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`hq-create-invoice-row-${idx}`}>
                  <input placeholder="Label" value={inst.label}
                    onChange={(e) => patchRow(idx, { label: e.target.value })}
                    className="col-span-4 px-3 py-2 rounded-lg border"/>
                  <input placeholder="0.00" value={inst.amount} inputMode="decimal"
                    onChange={(e) => patchRow(idx, { amount: e.target.value })}
                    className="col-span-3 px-3 py-2 rounded-lg border font-black"/>
                  <input type="date" value={inst.due_date}
                    onChange={(e) => patchRow(idx, { due_date: e.target.value })}
                    className="col-span-4 px-3 py-2 rounded-lg border"/>
                  <button type="button" onClick={() => removeRow(idx)} disabled={installments.length === 1}
                    className="col-span-1 h-10 rounded-lg border hover:border-[#C1350B] disabled:opacity-30 flex items-center justify-center">
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
              {preset === "custom" && (
                <button type="button" onClick={addRow} className="inline-flex items-center gap-1 text-[11px] font-black tracking-[0.14em] uppercase text-[#FF6A00]">
                  <Plus size={12}/> Add installment
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <Totem label="Invoice total"><span data-testid="hq-create-invoice-total-display">{money(totalMinor, currency)}</span></Totem>
              <Totem label="Scheduled">{money(scheduledMinor, currency)}</Totem>
              <Totem label="Remaining" tone={remaining === 0 ? "green" : "red"}>
                <span data-testid="hq-create-invoice-remaining">{money(remaining, currency)}</span>
              </Totem>
            </div>
          </Field>

          <Field label="Internal notes (not shown to customer)">
            <textarea data-testid="hq-create-invoice-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 rounded-lg border"/>
          </Field>

          {/* Phase 3H Part B — Project Agreement */}
          <div className="mt-2 border-t border-[#EAEAEA] pt-4">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="hq-create-invoice-as-project-row">
              <input
                type="checkbox" data-testid="hq-create-invoice-as-project"
                checked={asProject}
                onChange={(e) => {
                  setAsProject(e.target.checked);
                  if (e.target.checked && !projectTitle) setProjectTitle(description || "");
                }}
                className="mt-1"
              />
              <span className="text-[13px]">
                <span className="font-black">This is a bespoke project</span>
                <span className="block text-[11px] text-[#8A8A8A]">
                  Attach a governing Project Agreement (scope, deliverables, exclusions) — the customer must
                  accept it before the first payment. Later instalments reference the SAME accepted agreement.
                </span>
              </span>
            </label>

            {asProject && (
              <div className="mt-4 space-y-3" data-testid="hq-create-invoice-project-block">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Service / Agreement family">
                    <select
                      data-testid="hq-create-invoice-project-svc"
                      value={projectServiceType} onChange={(e) => setProjectSvc(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border"
                    >
                      {AGREEMENT_SERVICE_TYPES.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Customer country (ISO-2)">
                    <input
                      data-testid="hq-create-invoice-project-country"
                      value={projectCountry} onChange={(e) => setProjectCountry(e.target.value.toUpperCase().slice(0, 2))}
                      className="w-full px-3 py-2 rounded-lg border uppercase"
                      placeholder="GB"
                    />
                  </Field>
                </div>
                <Field label="Project title">
                  <input
                    data-testid="hq-create-invoice-project-title"
                    value={projectTitle} onChange={(e) => setProjectTitle(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border font-black"
                    placeholder="e.g. Acme Ltd — CRM & Business Systems Build"
                  />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Scope of work (one per line)">
                    <textarea
                      data-testid="hq-create-invoice-project-scope"
                      rows={4} value={projectScope} onChange={(e) => setProjectScope(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border text-[12px]"
                      placeholder={"Discovery workshop\nContacts + Deals modules\nCustom reporting dashboard"}
                    />
                  </Field>
                  <Field label="Deliverables (one per line)">
                    <textarea
                      data-testid="hq-create-invoice-project-deliverables"
                      rows={4} value={projectDeliverables} onChange={(e) => setProjectDeliverables(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border text-[12px]"
                      placeholder={"Deployed system\nAdmin training\n3 months support"}
                    />
                  </Field>
                  <Field label="Not included (one per line)">
                    <textarea
                      data-testid="hq-create-invoice-project-exclusions"
                      rows={4} value={projectExclusions} onChange={(e) => setProjectExclusions(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border text-[12px]"
                      placeholder={"Content entry\nHardware\nThird-party licences"}
                    />
                  </Field>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#EAEAEA] flex items-center justify-end gap-2">
          <button data-testid="hq-create-invoice-cancel" onClick={onClose} className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase">
            Cancel
          </button>
          <button
            data-testid="hq-create-invoice-save"
            onClick={saveDraft}
            disabled={!scheduleValid || saving || !toEmail}
            className={`px-4 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${scheduleValid && toEmail && !saving ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#F6F5F2] text-[#8A8A8A] cursor-not-allowed"}`}
          >
            {saving ? <Loader2 size={12} className="inline animate-spin mr-1"/> : null}
            Save as draft
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">{label}</div>
      {children}
    </div>
  );
}

function Totem({ label, tone, children }) {
  const cls = tone === "green" ? "text-[#0FA958]" : tone === "red" ? "text-[#C1350B]" : "text-black";
  return (
    <div className="rounded-lg border border-[#EAEAEA] px-3 py-2 text-center">
      <div className="text-[9px] tracking-[0.18em] uppercase font-bold text-[#8A8A8A]">{label}</div>
      <div className={`mt-1 font-black text-base ${cls}`}>{children}</div>
    </div>
  );
}
